import { Shield, Truck, HelpCircle } from "lucide-react";
import Logo from "./Logo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Jun-2026 (User-Wunsch): Footer kompakt harmonisiert — Rechts-Links als eine
// horizontale Zeile statt langer Spalte, reduzierte Abstände, realistische Aussagen.
const LEGAL_LINKS = [
    { to: "/legal/agb", label: "AGB", tid: "footer-agb-link" },
    { to: "/legal/datenschutz", label: "Datenschutz", tid: "footer-datenschutz-link" },
    { to: "/legal/impressum", label: "Impressum", tid: "footer-impressum-link" },
    { to: "/konto/meine-daten", label: "Meine Daten & Rechte", tid: "footer-meine-daten-link" },
    { to: "/legal/avv", label: "AVV (Händler)", tid: "footer-avv-link" },
    { to: "/legal/haendler-nutzungsbedingungen", label: "Händler-Nutzungsbedingungen", tid: "footer-dealer-terms-link" },
    { to: "/widerruf", label: "Widerrufsbelehrung", tid: "footer-widerruf-link" },
    { to: "/barrierefreiheit", label: "Barrierefreiheit", tid: "footer-barrierefreiheit-link" },
    { to: "/legal/compliance", label: "Compliance & Verfahren", tid: "footer-compliance-link" },
];

export default function Footer() {
    const { t } = useTranslation();
    return (
        <footer className="bg-[#111827] text-white mt-16" data-testid="site-footer">
            <div className="h-1.5 yellow-stripe" />
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="col-span-2">
                    <div className="flex items-center gap-2 mb-2">
                        <Logo className="contents" deClass="text-white/40" />
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed max-w-md">
                        {t("footer.tagline_long")}
                    </p>
                    <div className="mt-3 flex gap-2 text-xs">
                        <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-sm"><Shield className="w-3 h-3" /> {t("footer.badge_secure_payment")}</span>
                        <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-sm"><Truck className="w-3 h-3" /> {t("footer.badge_delivery")}</span>
                    </div>
                </div>
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2">{t("footer.col_categories")}</div>
                    <ul className="space-y-1.5 text-sm text-white/80">
                        <li><Link to="/search?category=motorcycle" className="hover:text-[#FFCC00]">{t("nav.motorcycles")}</Link></li>
                        <li><Link to="/search?category=bicycle" className="hover:text-[#FFCC00]">{t("nav.bicycles")}</Link></li>
                        <li><Link to="/search?category=motorcycle&bike_type=Naked" className="hover:text-[#FFCC00]">{t("footer.naked_bikes")}</Link></li>
                        <li><Link to="/search?category=bicycle&bike_type=Mountainbike" className="hover:text-[#FFCC00]">{t("footer.mountainbikes")}</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2">{t("footer.col_service")}</div>
                    <ul className="space-y-1.5 text-sm text-white/80">
                        <li><Link to="/dealers" className="hover:text-[#FFCC00]">{t("footer.find_dealers")}</Link></li>
                        <li><Link to="/was-kann-twowheels" className="hover:text-[#FFCC00]" data-testid="footer-was-kann-link">Was kann TwoWheels?</Link></li>
                        <li><Link to="/ki-features" className="hover:text-[#FFCC00]" data-testid="footer-ki-link">KI-Funktionen</Link></li>
                        <li><Link to="/hilfe" className="hover:text-[#FFCC00]" data-testid="footer-help-link"><HelpCircle className="w-3 h-3 inline" /> {t("footer.help")}</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10 py-4">
                <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col gap-2.5 text-xs text-white/50">
                    {/* Rechtliches — kompakt in einer Zeile (Jun-2026). */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="footer-legal-links">
                        <span className="text-white/70 uppercase tracking-wider text-[10px] font-bold">Rechtliches:</span>
                        {LEGAL_LINKS.map((l) => (
                            <Link key={l.to} to={l.to} className="text-white/60 hover:text-[#FFCC00]" data-testid={l.tid}>{l.label}</Link>
                        ))}
                    </div>
                    {/* Mai-2026 (User-Wunsch 21 May Iteration 12) — Profil-Registrierungs-Links auf jeder Seite. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="footer-register-links">
                        <span className="text-white/70 uppercase tracking-wider text-[10px] font-bold">Registrieren:</span>
                        <Link to="/register" className="text-white/60 hover:text-[#FFCC00]" data-testid="footer-reg-b2c">Privatkunde</Link>
                        <Link to="/register?role=b2b" className="text-white/60 hover:text-[#FFCC00]" data-testid="footer-reg-b2b">Händler</Link>
                        <Link to="/register?role=vermittler" className="text-white/60 hover:text-[#FFCC00]" data-testid="footer-reg-vermittler">Vermittler</Link>
                        <Link to="/register/service-provider" className="text-white/60 hover:text-[#FFCC00]" data-testid="footer-reg-dienstleister">Dienstleister</Link>
                        <Link to="/register/service-provider?branch=hersteller" className="text-white/60 hover:text-[#FFCC00]" data-testid="footer-reg-hersteller">Hersteller / Importeur</Link>
                        <Link to="/register/service-provider?branch=clubs_vereine" className="text-white/60 hover:text-[#FFCC00]" data-testid="footer-reg-club">Clubs / Vereine</Link>
                    </div>
                    {/* Markenrecht (User-Wunsch 30 Jun): Disclaimer unabhängiger Marktplatz. */}
                    <p className="text-white/40 text-[10px] leading-relaxed max-w-4xl" data-testid="footer-brand-disclaimer">
                        twowheels.info ist ein unabhängiger Online-Marktplatz. Genannte Marken-, Modell- und Herstellernamen
                        sind Eigentum der jeweiligen Rechteinhaber und dienen ausschließlich der Beschreibung der angebotenen
                        Fahrzeuge und Leistungen. Eine geschäftliche Verbindung zu oder Autorisierung durch die Hersteller
                        besteht nicht, soweit nicht ausdrücklich anders angegeben.
                    </p>
                    <div className="flex flex-col md:flex-row justify-between gap-1 text-[11px]">
                        <div>© {new Date().getFullYear()} twowheels.info — {t("footer.demo_marketplace")}</div>
                        <div>{t("footer.made_in")}</div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
